import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Chip from '@mui/material/Chip';

import StarIcon from '@icons/StarIcon/StarIcon';
import { CurrentSubscription } from '@app/types/payment/subscription';
import { SubscriptionStatus } from '@utils/enums.ts';
import { IconCancel } from '@tabler/icons-react';
import Tooltip from '@mui/material/Tooltip';
import { useMemo } from 'react';
import { dateFromString } from '@utils/utils.ts';

interface CurrentPlanCardProps {
  currentSubscription: CurrentSubscription | null;
  isReactivatePending: boolean;
  onUpgradePlan: () => void;
  onReactivatePlan: () => void;
  onCancelPlan: () => void;
  /**
   * The plan is the firm's, used by a member (ZLAYT-1617). Billing acts only
   * on a person's own plan, so no billing action is offered, just who to ask.
   */
  providedByFirm?: boolean;
}

function CurrentPlanCard({
  currentSubscription,
  isReactivatePending,
  onUpgradePlan,
  onReactivatePlan,
  onCancelPlan,
  providedByFirm = false,
}: CurrentPlanCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    subscription_name,
    description,
    status,
    is_trial,
    is_expired,
    days_remaining,
    price,
    cancel_at_period_end,
    ends_at,
  } = currentSubscription || {};

  const shouldShowCancelButton = useMemo(() => {
    return (
      price !== '0.00' &&
      !cancel_at_period_end &&
      ![SubscriptionStatus.CANCELED, SubscriptionStatus.NOT_ACTIVE].includes(
        status as SubscriptionStatus
      )
    );
  }, [price, cancel_at_period_end, status]);

  const isCancellationScheduled =
    status === SubscriptionStatus.ACTIVE && !!cancel_at_period_end && !!ends_at;

  const formattedEndsAt = ends_at ? dateFromString(new Date(ends_at)) : '';

  if (!currentSubscription) {
    return null;
  }

  return (
    <Stack spacing={1} flexGrow={1} width="100%">
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: theme.palette.primary.main,
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Stack spacing={1} direction="column">
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.light,
                }}
              >
                <StarIcon
                  width="20"
                  height="20"
                  stroke={theme.palette.primary.main}
                />
              </Box>

              <Typography variant="body1">
                {isCancellationScheduled
                  ? `${subscription_name} (Active until ${formattedEndsAt})`
                  : `${subscription_name} (${status})`}
              </Typography>
            </Stack>

            <Box>
              {is_trial && !is_expired && (
                <Chip
                  label={`Free Trial ${days_remaining} days remaining`}
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.light,
                  }}
                />
              )}

              {is_trial && is_expired && (
                <Chip
                  label="Trial Expired"
                  size="small"
                  sx={{
                    color: theme.palette.error.main,
                    backgroundColor: theme.palette.error.light,
                  }}
                />
              )}

              {status === SubscriptionStatus.NOT_ACTIVE && (
                <Chip
                  label="Your subscription is not active"
                  size="small"
                  sx={{
                    color: theme.palette.warning.main,
                    backgroundColor: theme.palette.warning.light,
                  }}
                />
              )}

              {((status === SubscriptionStatus.CANCELED &&
                cancel_at_period_end) ||
                isCancellationScheduled) && (
                <Stack spacing={1}>
                  <Box>
                    <Chip
                      label={`Your subscription will end on ${formattedEndsAt}`}
                      size="small"
                      sx={{
                        color: theme.palette.error.main,
                        backgroundColor: theme.palette.error.light,
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption">
                      You still have full access to all features of your plan
                      until the end of the current billing cycle. You can
                      reactivate your plan at any time.
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Box>

            {description && (
              <Typography paddingLeft={0.5} variant="caption">
                {description}
              </Typography>
            )}

            {providedByFirm && (
              <Typography
                data-testid="plan-provided-by-firm"
                paddingLeft={0.5}
                variant="body2"
                color="text.secondary"
              >
                {`Your plan is provided by your firm (${subscription_name}). Ask your firm's admin about changes.`}
              </Typography>
            )}
          </Stack>
        </Box>

        {!providedByFirm && (
          <Stack direction="row" spacing={2}>
            {/* Free plan - show View Plans */}
            {price === '0.00' && (
              <Button variant="contained" onClick={onUpgradePlan}>
                Upgrade Plan
              </Button>
            )}

            {!isReactivatePending &&
              price !== '0.00' &&
              (isCancellationScheduled ||
                [
                  SubscriptionStatus.CANCELED,
                  SubscriptionStatus.NOT_ACTIVE,
                ].includes(status as SubscriptionStatus)) && (
                <>
                  <Button variant="contained" onClick={onReactivatePlan}>
                    Reactivate
                  </Button>
                  <Button variant="outlined" onClick={onUpgradePlan}>
                    Upgrade Plan
                  </Button>
                </>
              )}

            {/* Active paid - always allow viewing/upgrading plans, even if cancel_at_period_end is true */}
            {price !== '0.00' &&
              status === SubscriptionStatus.ACTIVE &&
              !isCancellationScheduled && (
                <Button variant="outlined" onClick={onUpgradePlan}>
                  Upgrade Plan
                </Button>
              )}
          </Stack>
        )}
      </Stack>

      {!providedByFirm && shouldShowCancelButton && (
        <Box>
          <Tooltip title="If you cancel your plan, you will lose access to all paid features.">
            <Button
              size="small"
              color="error"
              startIcon={<IconCancel size={16} />}
              sx={{
                minHeight: 0,
              }}
              onClick={onCancelPlan}
            >
              Cancel subscription
            </Button>
          </Tooltip>
        </Box>
      )}
    </Stack>
  );
}

export default CurrentPlanCard;
